// Helper function to generate flag emojis from ISO codes
function getFlagEmoji(countryCode: string): string {
  return countryCode
    .toUpperCase()
    .split('')
    .map((char) => String.fromCodePoint(127397 + char.charCodeAt(0)))
    .join('');
}

interface LocalizationProps {
  localizationAnabled: boolean;
}

// Check if the custom element is already defined
if (!customElements.get('localization-section')) {
  customElements.define(
    'localization-section',
    class ExampleSection extends HTMLElement {
      private dataEl: HTMLElement | null;
      private data: LocalizationProps;
      private modal: HTMLElement | null;
      private modalContent: HTMLElement | null;
      private closeButton: HTMLElement | null;

      constructor() {
        super();
        this.dataEl = this.querySelector('[data-section-data]');
        this.modal = this.querySelector('[data-localization]');
        this.modalContent = this.querySelector('[data-localization-content]');
        this.closeButton = this.querySelector('[data-close-localization]');
        this.data = this.dataEl?.textContent
          ? JSON.parse(this.dataEl.textContent)
          : { localizationAnabled: false };
      }

      // Show the modal
      showModal(): void {
        if (this.modal && this.modalContent) {
          document.body.classList.add('overflow-hidden');
          this.modal.classList.remove('hidden');
          this.modal.classList.add('flex');
          this.modalContent.classList.remove('scale-out');
          this.modalContent.classList.add('scale-in');
        }
      }

      // Hide the modal
      hideModal(): void {
        if (this.modal && this.modalContent) {
          document.body.classList.remove('overflow-hidden');

          // Add scale-out for animation
          this.modalContent.classList.remove('scale-in');
          this.modalContent.classList.add('scale-out');

          // Wait for animation to complete before hiding the modal
          setTimeout(() => {
            if (this.modal) {
              this.modal.classList.add('hidden');
              this.modal.classList.remove('flex');
            }
          }, 300);
        }
      }

      // Add flags
      updateFlagsInUI(): void {
        if (!this.modal) return;
        // Update dropdown options
        const selectElement = this.modal.querySelector('[data-localization-country-select]') as HTMLSelectElement;
        if (selectElement) {
          Array.from(selectElement.options).forEach((option) => {
            const countryCode = option.value;
            const countryName = option.getAttribute('data-localization-country-name') || '';
            option.textContent = `${getFlagEmoji(countryCode)} ${countryName}`;
          });
        }

        // Update buttons with flags
        const localizationButtons = document.querySelectorAll('[data-open-localization]');
        if (localizationButtons.length) {
          localizationButtons.forEach((button) => {
            const countryCode = button.getAttribute('data-localization-country-code');
            if (countryCode) {
              button.textContent = getFlagEmoji(countryCode);
            }
          });
        }

        // Update store flags dynamically in links
        const localizationFlagElements = this.modal.querySelectorAll('[data-localization-store-flag]');
        if (localizationFlagElements.length) {
          localizationFlagElements.forEach((element) => {
            const countryCode = element.getAttribute('data-localization-store-flag');
            if (countryCode) {
              element.textContent = getFlagEmoji(countryCode);
            }
          });
        }
      }

      closeOnOverlayClick(event: MouseEvent): void {
        if (event.target === this.modal) {
          this.hideModal();
        }
      }

      init(): void {
        if (!this.data.localizationAnabled) return;

        // Add flags to dropdown and opne buttons
        this.updateFlagsInUI();

        // Add event listeners to open buttons
        const openButtons = document.querySelectorAll('[data-open-localization]');
        openButtons.forEach((button) => {
          button.addEventListener('click', () => this.showModal());
        });

        // Add event listener to the close button
        this.closeButton?.addEventListener('click', () => this.hideModal());

        // Add event listener to the overlay for closing when clicked
        this.modal?.addEventListener('click', (event) => this.closeOnOverlayClick(event));
      }

      // Lifecycle callback when the element is connected to the DOM
      connectedCallback(): void {
        this.init();
      }

      disconnectedCallback(): void {
        const openButtons = document.querySelectorAll('[data-open-localization]');
        openButtons.forEach((button) => {
          button.removeEventListener('click', () => this.showModal());
        });
        this.closeButton?.removeEventListener('click', () => this.hideModal());
        this.modal?.removeEventListener('click', (event) => this.closeOnOverlayClick(event));
      }
    }
  );
}
